// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CookiePrivacyPolicyPopup from "@insite/content-library/Components/CookiePrivacyPolicyPopup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LiveChat from "@rugby/App/Components/LiveChat/LiveChat";
import { screen } from "@rugby/XDL/Helpers";
import { extendedMaxWidths } from "@rugby/XDL/PreStyle/breakpoints";
import { footerColor } from "@rugby/XDL/PreStyle/colors";
import { MediaQuery } from "@rugby/XDL/Utilities";
import * as React from "react";
import styled, { css } from "styled-components";

export const FooterDiv = styled.div`
    background-color: ${footerColor.main};
    padding: 64px 0;

    ${screen("lte", "sm")`
        padding: 10px 0;
    `}
`;

export interface FooterStyles {
    page?: MobiusPageProps;
    mainContainer?: InjectableCss;
    footer?: InjectableCss;
    footNote?: InjectableCss;
    footNoteGridContainer?: InjectableCss;
    footNoteCopyright?: InjectableCss;
    footNoteLinks?: InjectableCss;
}

export const footerStyles: FooterStyles = {
    mainContainer: {
        css: css`
            background: ${getColor("common.white")};
            padding-top: 50px;
            ${MediaQuery.lte("md")} {
                padding-top: 30px;
            }
            @media print {
                display: none;
            }
        `,
    },
    footer: {
        css: css`
            background-color: ${footerColor.main};
        `,
    },
    footNote: {
        css: css`
            padding: 18px 0;
            display: flex;
            justify-content: space-between;
            max-width: ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 18px 10px;
                        `,
                        css`
                            padding: 18px 10px;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[2]}px;
                            margin: 0 auto;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[3]}px;
                            margin: 0 auto;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[4]}px;
                            margin: 0 auto;
                        `,
                    ],
                    "min",
                )};
        `,
    },
    footNoteCopyright: {
        css: css`
            color: ${getColor("text.main")};
            font-size: 14px;
            ${screen("lte", "xs")`
                padding-left: 15px;
            `};
        `,
    },
    footNoteLinks: {
        css: css`
            color: #000;

            .linkList {
                text-align: right;
                font-size: 14px;
                padding: 0 0;
                .titleWrapper {
                    display: none;
                }
                .link {
                    padding: 0 15px 0 0;
                    margin: 0 15px 0 0;
                    text-transform: capitalize;

                    position: relative;

                    &:after {
                        content: "";
                        width: 1px;
                        height: 50%;
                        top: 50%;
                        transform: translateY(-50%);
                        position: absolute;
                        right: 0;
                        background: ${getColor("common.border")};
                    }

                    ${screen("lte", "xs")`
                        margin: 0;

                        &:after {
                            content: none;
                        }
                    `};

                    &:last-child:after {
                        content: none;
                        display: none;
                    }

                    a {
                        color: ${getColor("secondary.main")};
                        font-size: 14px;
                        letter-spacing: 0;
                        line-height: 16px;

                        &:hover {
                            text-decoration: underline;
                        }
                    }

                    a[class^="StyledButton"] span[class^="TypographyStyle"] {
                        font-size: 14px;
                        letter-spacing: 0;
                        line-height: 16px;
                        color: ${getColor("secondary.main")};
                    }
                }
            }
        `,
    },
};

const styles = footerStyles;

const Footer = ({ id }: PageProps) => (
    <StyledWrapper {...footerStyles.mainContainer}>
        <FooterDiv>
            <Page as="footer" isFooter={true} background="transparent" fullWidth={[true, true, true, true, true]} {...styles.page}>
                <Zone contentId={id} zoneName="Content" />
                <CookiePrivacyPolicyPopup />
            </Page>
        </FooterDiv>
        <StyledWrapper {...footerStyles.footNote}>
            <StyledWrapper {...footerStyles.footNoteCopyright}>
                <Zone zoneName="footNoteCopyright" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...footerStyles.footNoteLinks}>
                <Zone zoneName="footNoteLinks" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
        <LiveChat />
    </StyledWrapper>
);

const pageModule: PageModule = {
    component: Footer,
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "Footer" instead of this constant.
 */
export const FooterContext = "Footer";
